import { multiSortComparator, type SortingState } from "../../../utils/multiColumnSorting";
import type { PrintoutColumn, PrintoutColumnType, ScheduleRow } from "../types";

/**
 * Within-section helpers that need to walk rows by their merge-column values.
 *
 * The merge column "key" is a **stable equality key** — not the same as the cell's rendered value.
 * In particular:
 * - Shift Time → `${start.toISO()}~${end.toISO()}` (deterministic, sub-second precision)
 * - Role/Job under JobManagement → `jobId` (stable identity; two jobs with identical display
 *   names never accidentally merge)
 * - Certification → the raw role code (not the short code)
 *
 * Used for: vertical-merge boundaries (`applyVerticalMerges`), `staffPerRow` batch boundaries
 * (`composeMergeGroupKey`), and intra-section sort (`sortRowsByMergeColumns`). The cell renderer
 * uses its own display values via `populateCell` — keep these two concerns separate.
 */
const getMergeColumnKey = (row: ScheduleRow, colType: PrintoutColumnType, jobManagementEnabled: boolean): string => {
	switch (colType) {
		case "location":
			return row.location || "";
		case "certification":
			return row.nurse?.role || row.role || "";
		case "role":
			if (jobManagementEnabled) return row.nurse?.jobId ?? "";
			return row.nurse?.role ?? row.role ?? "";
		case "shiftTime":
			return `${row.shiftStart.toISO()}~${row.shiftEnd.toISO()}`;
		case "floor":
			return row.floor || "";
		case "name":
			return row.nurse?.name || "";
		default:
			return "";
	}
};

/**
 * Composite key across multiple merge columns. Used for staffPerRow batch-boundary detection.
 */
export const composeMergeGroupKey = (row: ScheduleRow, mergeCols: PrintoutColumnType[], jobManagementEnabled: boolean): string => {
	return mergeCols.map((colType) => getMergeColumnKey(row, colType, jobManagementEnabled)).join("|");
};

/**
 * Reorder `rowGroupingBy` to match the left-to-right column order from `enabledColumns`. The
 * leftmost merge column becomes the primary sort key — preserves today's "merge columns appear
 * left-to-right in the user's chosen column order" behavior.
 */
export const orderMergeColumns = (rowGroupingBy: PrintoutColumnType[], enabledColumns: PrintoutColumn[]): PrintoutColumnType[] => {
	if (rowGroupingBy.length === 0) return rowGroupingBy;
	return [...rowGroupingBy].sort((a, b) => enabledColumns.findIndex((c) => c.type === a) - enabledColumns.findIndex((c) => c.type === b));
};

interface SortByMergeColumnsArgs {
	rows: ScheduleRow[];
	mergeCols: PrintoutColumnType[];
	enabledColumns: PrintoutColumn[];
	sortBy: PrintoutColumn[] | undefined;
	customStringOrderMap: Map<string, number> | null | undefined;
	jobManagementEnabled: boolean;
}

/**
 * Stable-sort the rows by their merge-column values, using the shared `compareValues` chain.
 *
 * **Why this exists — merge boundary stability.** `applyVerticalMerges` (and the `staffPerRow > 1`
 * batch-packing logic) collapse cells with the same merge-column value into one merged cell only
 * when those cells are *adjacent*. Phase 2's row sort honors the user's `sortBy` columns, but if
 * `rowGroupingBy` includes a column not in `sortBy` — or if `sortBy` orders rows in a way that
 * interleaves merge values — adjacency is not guaranteed. This re-sort clusters rows by their
 * merge-column values so every same-value run is contiguous, making the downstream merge
 * collapse-able.
 *
 * **Why we still need `sortBy`.** Clustering itself is direction-agnostic (asc or desc both
 * produce contiguous runs), but the user expects their explicit direction to flow through —
 * `location desc` should reverse cluster order. So we look up direction per column from `sortBy`
 * and apply it as the comparator's `sortOrder`. Compatible with mapping §5.8 since `compareValues`
 * is still the single comparator used everywhere else; this layer only changes ordering, not
 * comparison semantics.
 */
export const sortRowsByMergeColumns = (args: SortByMergeColumnsArgs): ScheduleRow[] => {
	const { rows, mergeCols, enabledColumns, sortBy, customStringOrderMap, jobManagementEnabled } = args;
	if (mergeCols.length === 0) return rows;
	const ordered = orderMergeColumns(mergeCols, enabledColumns);

	// Build per-row projection keyed by each merge column. Project once up-front to avoid
	// re-extracting on every comparator call.
	type Projection = Record<string, string>;
	const projections = new Map<ScheduleRow, Projection>();
	for (const row of rows) {
		const projection: Projection = {};
		for (const colType of ordered) {
			projection[colType] = getMergeColumnKey(row, colType, jobManagementEnabled);
		}
		projections.set(row, projection);
	}

	// Direction lookup once per column.
	const directionFor = (colType: PrintoutColumnType): "asc" | "desc" => {
		const found = sortBy?.find((s) => s.enabled && s.type === colType);
		return found?.sortDirection === "desc" ? "desc" : "asc";
	};

	const sortState: SortingState<Projection>[] = ordered.map((colType) => ({
		sortColumns: [colType],
		sortOrder: directionFor(colType),
	}));

	const comparator = multiSortComparator<Projection>(sortState, customStringOrderMap);
	const paired = rows.map((row) => ({ row, projection: projections.get(row) as Projection }));
	paired.sort((a, b) => comparator(a.projection, b.projection));
	return paired.map((p) => p.row);
};
